// ── Chart.js helper ────────────────────────────
let _chartLoaded = false;
let _chartPromise = null;

export function loadChartJs() {
  if (_chartLoaded && window.Chart) return Promise.resolve(window.Chart);
  if (_chartPromise) return _chartPromise;
  _chartPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    // Use the auto-registering bundle
    s.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.7';
    s.onload = () => {
      const C = window.Chart;
      if (!C) { reject('Chart.js not loaded'); return; }
      // Auto-register if needed
      if (C.registerables && C.registry && C.registry.controllers.size === 0) {
        C.register(...C.registerables);
      }
      _chartLoaded = true;
      resolve(C);
    };
    s.onerror = () => reject('Failed to load Chart.js');
    document.head.appendChild(s);
  });
  return _chartPromise;
}

// ── Reusable Chart component ────────────────────
import React from 'react';

export const ChartCanvas = ({type, data, options, height = 280}) => {
  const containerRef = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    let cancelled = false;
    
    // Only render if we have actual data
    if (!data || !data.labels || data.labels.length === 0) return;

    loadChartJs().then((Chart) => {
      if (cancelled || !containerRef.current) return;

      // Destroy previous
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

      // Clear container and create canvas
      const container = containerRef.current;
      container.innerHTML = '';
      const canvas = document.createElement('canvas');
      container.appendChild(canvas);

      // Deep clone data to prevent Chart.js mutation
      const clonedData = JSON.parse(JSON.stringify(data));

      chartRef.current = new Chart(canvas, {
        type,
        data: clonedData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 500 },
          layout: { padding: { top: 6, bottom: 6, left: 4, right: 4 } },
          plugins: {
            legend: {
              display: options?.plugins?.legend?.display !== false,
              position: options?.plugins?.legend?.position || 'bottom',
              labels: {
                font: { size: 11, family: "'DM Sans', system-ui, sans-serif" },
                padding: 14, usePointStyle: true, pointStyle: 'circle',
                ...(options?.plugins?.legend?.labels || {}),
              },
            },
            tooltip: {
              backgroundColor: 'rgba(26,30,74,0.95)',
              titleFont: { size: 12, family: "'DM Sans', system-ui" },
              bodyFont: { size: 11, family: "'DM Sans', system-ui" },
              padding: 12, cornerRadius: 8,
              borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
              ...(options?.plugins?.tooltip || {}),
            },
          },
          scales: options?.scales || {},
          indexAxis: options?.indexAxis,
          cutout: options?.cutout,
        }
      });
    }).catch(err => console.error('ChartCanvas error:', err));

    return () => {
      cancelled = true;
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  }, [JSON.stringify(data?.labels), JSON.stringify(data?.datasets?.map(d=>d.data)), type]);

  return (
    <div ref={containerRef}
      style={{position:'relative', width:'100%', height: height + 'px'}}/>
  );
};
